import React, { useState, useMemo, useRef, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { saveAs } from "file-saver";
import MonthPickerModal from "../../components/MonthPickerModal/MonthPickerModal";
import "../AgenteControlloRendicontazioni.css";
import "../AIAgents.css";
import "../Upload.css";
import "./SezioneMulte.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// --- Helper filtri (stessa logica del tab Ore: opzioniDaCampoMulti/matchCampoMulti) ---
const opzioniDaCampoMulti = (righe, campo) => {
    const set = new Set();
    for (const r of righe || []) {
        String(r[campo] || "")
            .split(/,\s*/)
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((v) => set.add(v));
    }
    return [...set]
        .sort((a, b) => a.localeCompare(b, "it-IT", { numeric: true }))
        .map((v) => ({ value: v, label: v }));
};

const matchCampoMulti = (rigaValue, selezionati) => {
    if (!selezionati || selezionati.length === 0) return true;
    const sel = new Set(selezionati.map((o) => o.value));
    return String(rigaValue || "")
        .split(/,\s*/)
        .map((s) => s.trim())
        .filter(Boolean)
        .some((v) => sel.has(v));
};

const fmtEuro = (v) => (v != null && v !== "" ? `${Number(v).toFixed(2)} €` : "-");

// Mese corrente (YYYY-MM): default per la Sintesi Multe.
const meseCorrenteYM = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtDataOra = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// =====================================================================
// SINTESI MULTE
// =====================================================================
const SintesiMulte = () => {
    const [mese, setMese] = useState("");
    const [righe, setRighe] = useState(null); // null = report non ancora generato
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState("");
    // Filtri "bozza" (legati ai dropdown)
    const [filialeSel, setFilialeSel] = useState([]);
    const [tipoContrattoSel, setTipoContrattoSel] = useState([]);
    const [settoreSel, setSettoreSel] = useState([]);
    const [commessaSel, setCommessaSel] = useState([]);
    // Filtri applicati alla tabella (commit su "Applica")
    const [filtriAppl, setFiltriAppl] = useState({ filiale: [], tipoContratto: [], settore: [], commessa: [] });
    const [espansi, setEspansi] = useState(new Set());

    const handleApplica = async () => {
        if (loading) return;
        setLoading(true);
        setErrore("");
        try {
            const { data } = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/multe/storico`,
                { mese }
            );
            setRighe(Array.isArray(data.sintesi) ? data.sintesi : []);
            setFiltriAppl({
                filiale: filialeSel,
                tipoContratto: tipoContrattoSel,
                settore: settoreSel,
                commessa: commessaSel,
            });
            setEspansi(new Set());
        } catch (err) {
            setErrore(err.response?.data?.error || err.message || "Errore durante la generazione");
            setRighe(null);
        } finally {
            setLoading(false);
        }
    };

    // Auto-caricamento all'atterraggio (mese di default = corrente).
    useEffect(() => {
        handleApplica();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const opzioniFiliale = useMemo(() => opzioniDaCampoMulti(righe, "filiale"), [righe]);
    const opzioniTipoContratto = useMemo(() => opzioniDaCampoMulti(righe, "tipo_contratto"), [righe]);
    const opzioniSettore = useMemo(() => opzioniDaCampoMulti(righe, "settore"), [righe]);
    const opzioniCommessa = useMemo(() => opzioniDaCampoMulti(righe, "commessa"), [righe]);

    const filtered = useMemo(() => {
        if (!righe) return [];
        return righe.filter((r) =>
            matchCampoMulti(r.filiale, filtriAppl.filiale) &&
            matchCampoMulti(r.tipo_contratto, filtriAppl.tipoContratto) &&
            matchCampoMulti(r.settore, filtriAppl.settore) &&
            matchCampoMulti(r.commessa, filtriAppl.commessa)
        );
    }, [righe, filtriAppl]);

    const toggleEspandi = (key) => {
        setEspansi((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <>
            <div className="acr-panel">
                <div className="acr-filters-grid">
                    <div className="acr-filter-item">
                        <label>Data Caricamento</label>
                        <div className="storico-mese-wrapper">
                            <MonthPickerModal
                                value={mese}
                                onChange={(e) => setMese(e.target.value)}
                                label="Seleziona mese"
                                min="2026-01"
                            />
                            {mese && (
                                <button
                                    className="storico-mese-clear"
                                    onClick={() => setMese("")}
                                    title="Tutti i periodi"
                                    type="button"
                                >×</button>
                            )}
                        </div>
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Filiale</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniFiliale}
                            value={filialeSel}
                            onChange={(vals) => setFilialeSel(vals || [])}
                            placeholder={righe ? "Tutte" : "Genera report…"}
                            noOptionsMessage={() => "Nessuna filiale"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Tipo Contratto</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniTipoContratto}
                            value={tipoContrattoSel}
                            onChange={(vals) => setTipoContrattoSel(vals || [])}
                            placeholder={righe ? "Tutti" : "Genera report…"}
                            noOptionsMessage={() => "Nessun tipo"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Settore</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniSettore}
                            value={settoreSel}
                            onChange={(vals) => setSettoreSel(vals || [])}
                            placeholder={righe ? "Tutti" : "Genera report…"}
                            noOptionsMessage={() => "Nessun settore"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Commessa</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniCommessa}
                            value={commessaSel}
                            onChange={(vals) => setCommessaSel(vals || [])}
                            placeholder={righe ? "Tutte" : "Genera report…"}
                            noOptionsMessage={() => "Nessuna commessa"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-actions">
                        <button className="acr-btn-primary" onClick={handleApplica} disabled={loading}>
                            {loading ? "Caricamento…" : "Applica"}
                        </button>
                    </div>
                </div>
            </div>

            {errore && <div className="acr-errore">{errore}</div>}

            {righe && (
                <div className="acr-table-wrapper">
                    <table className="acr-table multe-sintesi-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Data caricamento</th>
                                <th>Nome</th>
                                <th>Cognome</th>
                                <th>Filiale</th>
                                <th>Tipo Contratto</th>
                                <th>Settore</th>
                                <th>Commessa</th>
                                <th>N. Verbale</th>
                                <th>Importo Totale</th>
                                <th>N. Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="multe-empty">Nessuna multa per i filtri selezionati.</td>
                                </tr>
                            )}
                            {filtered.map((r) => {
                                const key = `${r.n_verbale}|${r.nome}|${r.cognome}`;
                                const aperto = espansi.has(key);
                                return (
                                    <React.Fragment key={key}>
                                        <tr className={r.rateizzazione_troncata ? "multa-row-troncata" : ""}>
                                            <td className="expand-cell-compact">
                                                <button
                                                    className="expand-btn-compact"
                                                    onClick={() => toggleEspandi(key)}
                                                    title={aperto ? "Comprimi" : "Espandi rateizzazione"}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        style={{
                                                            transform: aperto ? "rotate(180deg)" : "rotate(0deg)",
                                                            transition: "transform 0.2s",
                                                        }}
                                                    >
                                                        <polyline points="6 9 12 15 18 9" />
                                                    </svg>
                                                </button>
                                            </td>
                                            <td>{fmtDataOra(r.created_at)}</td>
                                            <td>{r.nome || "-"}</td>
                                            <td>{r.cognome || "-"}</td>
                                            <td>{r.filiale || "-"}</td>
                                            <td>{r.tipo_contratto || "-"}</td>
                                            <td>{r.settore || "-"}</td>
                                            <td>{r.commessa || "-"}</td>
                                            <td>{r.n_verbale || "-"}</td>
                                            <td className="multe-importo">{fmtEuro(r.importo_totale)}</td>
                                            <td>{r.num_rate != null ? r.num_rate : (r.rate?.length || "-")}</td>
                                        </tr>
                                        {aperto && (
                                            <tr className="expanded-row-compact">
                                                <td colSpan={11}>
                                                    <div className="expanded-content-compact">
                                                        <h5 className="multe-rate-title">Rateizzazione</h5>
                                                        {r.rate && r.rate.length > 0 ? (
                                                            <table className="acr-table multe-rate-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Rata</th>
                                                                        <th>Mese</th>
                                                                        <th>Importo</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {r.rate.map((rata) => (
                                                                        <tr key={rata.rata_numero}>
                                                                            <td>{rata.rata_numero}</td>
                                                                            <td>{rata.mese || "-"}</td>
                                                                            <td className="multe-importo">{fmtEuro(rata.importo)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        ) : (
                                                            <p className="multe-no-rate">Nessuna rateizzazione registrata.</p>
                                                        )}
                                                        {r.rateizzazione_troncata && r.data_fine_contratto && (
                                                            <div className="multa-warning-rateizzazione">
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05l-8.47-14.14a2 2 0 0 0-3.42 0z" />
                                                                    <line x1="12" y1="9" x2="12" y2="13" />
                                                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                                                </svg>
                                                                <div>
                                                                    <strong>Rateizzazione troncata per scadenza contratto</strong>
                                                                    <p>{new Date(r.data_fine_contratto).toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" })}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

// =====================================================================
// CARICAMENTO MULTE
// =====================================================================
const STATO_OPERATORE_LABEL = {
    risolto: { testo: "Operatore associato", cls: "ok" },
    non_trovato: { testo: "Operatore non associato", cls: "warn" },
    ambiguo: { testo: "Operatore ambiguo (omonimia)", cls: "warn" },
};

const isSalvabile = (item) =>
    !!(item?.data?.numero_verbale && item?.data?.nome && item?.data?.cognome);

const MultaCard = ({ item, operatoriOptions, onSalva, onElimina, onSelezionaOperatore }) => {
    const { data, operatore, errore, _stato, _erroreSalva, _operatoreManuale } = item;

    if (errore) {
        return (
            <div className="multa-card multa-card-error">
                <div className="multa-card-head">
                    <span className="multa-card-file">{item.file}</span>
                </div>
                <div className="upload-message error">{errore}</div>
                <div className="multa-azioni">
                    <button className="multa-btn-elimina" onClick={() => onElimina(item._key)}>
                        Elimina
                    </button>
                </div>
            </div>
        );
    }

    const salvabile = isSalvabile(item);
    const salvata = _stato === "saved";
    const gia = _stato === "gia";
    const salvando = _stato === "saving";
    const salvaDisabled = salvando || salvata || gia || !salvabile;

    const tooltipSalva = gia
        ? "Multa già elaborata e caricata a sistema"
        : !salvabile
        ? "Dati incompleti: impossibile salvare"
        : "";

    const risolto = operatore?.stato === "risolto";
    const statoOp = operatore ? STATO_OPERATORE_LABEL[operatore.stato] : null;
    const bloccaModifica = salvata || gia || salvando;

    return (
        <div className="multa-card">
            <div className="multa-card-head">
                <span className="multa-card-file">{item.file}</span>
                {salvata && <span className="multa-badge ok">Salvata</span>}
                {gia && <span className="multa-badge gia">Già a sistema</span>}
            </div>

            <div className="ai-risultato">
                <div className="ai-risultato-grid">
                    <div className="ai-risultato-item">
                        <span className="ai-risultato-label">Nome</span>
                        <span className="ai-risultato-value">{data.nome || "-"}</span>
                    </div>
                    <div className="ai-risultato-item">
                        <span className="ai-risultato-label">Cognome</span>
                        <span className="ai-risultato-value">{data.cognome || "-"}</span>
                    </div>
                    <div className="ai-risultato-item">
                        <span className="ai-risultato-label">Importo Totale</span>
                        <span className="ai-risultato-value ai-importo">{fmtEuro(data.importo_totale)}</span>
                    </div>
                    <div className="ai-risultato-item">
                        <span className="ai-risultato-label">N. Verbale</span>
                        <span className="ai-risultato-value">{data.numero_verbale || "-"}</span>
                    </div>
                </div>

                {risolto && statoOp && (
                    <div className={`multa-operatore ${statoOp.cls}`}>{statoOp.testo}</div>
                )}

                {!risolto && (
                    <div className="multa-operatore-manuale">
                        <div className={`multa-operatore ${_operatoreManuale ? "ok" : "warn"}`}>
                            {_operatoreManuale
                                ? "Operatore assegnato manualmente"
                                : statoOp?.testo || "Operatore non associato"}
                        </div>
                        <label className="multa-operatore-label">Assegna operatore (anagrafica GECOS)</label>
                        <Select
                            options={operatoriOptions}
                            value={_operatoreManuale || null}
                            onChange={(opt) => onSelezionaOperatore(item._key, opt)}
                            isClearable
                            isDisabled={bloccaModifica}
                            placeholder="Cerca operatore…"
                            noOptionsMessage={() => "Nessun operatore"}
                            classNamePrefix="acr-select"
                        />
                    </div>
                )}

                {data.rate && data.rate.length > 0 && (
                    <div className="ai-rate-section">
                        <h5 className="ai-rate-title">Rateizzazione ({data.num_rate} rate)</h5>
                        <table className="ai-rate-table">
                            <thead>
                                <tr>
                                    <th>Rata</th>
                                    <th>Mese</th>
                                    <th>Importo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rate.map((rata, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{rata.mese}</td>
                                        <td className="ai-importo">{fmtEuro(rata.importo)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="multa-azioni">
                <button
                    className="multa-btn-salva"
                    onClick={() => onSalva(item._key)}
                    disabled={salvaDisabled}
                    title={tooltipSalva}
                >
                    {salvando ? "Salvataggio…" : salvata ? "Salvata" : "Salva"}
                </button>
                <button
                    className="multa-btn-elimina"
                    onClick={() => onElimina(item._key)}
                    disabled={salvando}
                >
                    Elimina
                </button>
                {_erroreSalva && <span className="multa-salva-err">{_erroreSalva}</span>}
            </div>
        </div>
    );
};

const CaricamentoMulte = () => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [risultati, setRisultati] = useState(null);
    const [errore, setErrore] = useState("");
    const [salvandoTutti, setSalvandoTutti] = useState(false);
    const [operatoriOptions, setOperatoriOptions] = useState([]);

    // Mirror sincrono dei risultati per la lettura durante "Salva tutti" sequenziale.
    const risultatiRef = useRef([]);
    useEffect(() => {
        risultatiRef.current = risultati || [];
    }, [risultati]);

    // Anagrafica GECOS per l'assegnazione manuale (caricata una volta).
    useEffect(() => {
        let attivo = true;
        axios
            .get(`${API_URL}/agente-controllo-rendicontazioni/multe/operatori`)
            .then(({ data }) => {
                if (!attivo) return;
                const opts = (data.operatori || []).map((o) => ({
                    value: o.id,
                    label: `${o.cognome} ${o.nome}`,
                }));
                setOperatoriOptions(opts);
            })
            .catch(() => {});
        return () => {
            attivo = false;
        };
    }, []);

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files || []));
        setRisultati(null);
        setErrore("");
    };

    const handleAnalizza = async () => {
        if (files.length === 0 || uploading) return;
        setUploading(true);
        setErrore("");
        setRisultati(null);
        try {
            const formData = new FormData();
            files.forEach((f) => formData.append("files", f));
            const { data } = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/multe/analyze`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            const items = (Array.isArray(data.risultati) ? data.risultati : []).map((r, i) => ({
                ...r,
                _key: `${i}_${r.file || "doc"}`,
                _stato: r.errore ? "errore" : r.alreadyExists ? "gia" : "idle",
                _erroreSalva: "",
            }));
            setRisultati(items);
        } catch (err) {
            setErrore(err.response?.data?.error || "Errore durante l'elaborazione dei documenti");
        } finally {
            setUploading(false);
        }
    };

    const handleReset = () => {
        setFiles([]);
        setRisultati(null);
        setErrore("");
        const input = document.getElementById("multe-file-input");
        if (input) input.value = "";
    };

    // Aggiorna un item (per _key) sia nello state sia nel ref sincrono.
    const aggiornaItem = (key, patch) => {
        setRisultati((prev) => {
            const next = (prev || []).map((it) => (it._key === key ? { ...it, ...patch } : it));
            risultatiRef.current = next;
            return next;
        });
    };

    // Assegnazione manuale operatore (per le multe senza match automatico).
    const selezionaOperatore = (key, opt) => {
        aggiornaItem(key, { _operatoreManuale: opt || null });
    };

    const salva = async (key) => {
        const item = risultatiRef.current.find((it) => it._key === key);
        if (!item || item._stato !== "idle" || !isSalvabile(item)) return;
        aggiornaItem(key, { _stato: "saving", _erroreSalva: "" });
        try {
            await axios.post(`${API_URL}/agente-controllo-rendicontazioni/multe/save`, {
                n_verbale: item.data.numero_verbale,
                nome: item.data.nome,
                cognome: item.data.cognome,
                id_operatore: item._operatoreManuale?.value ?? item.operatore?.id ?? null,
                importo_totale: item.data.importo_totale,
                num_rate: item.data.num_rate,
                file_name: item.file,
                rate: item.data.rate || [],
            });
            aggiornaItem(key, { _stato: "saved" });
        } catch (err) {
            if (err.response?.status === 409) {
                aggiornaItem(key, { _stato: "gia" });
            } else {
                aggiornaItem(key, {
                    _stato: "idle",
                    _erroreSalva: err.response?.data?.error || "Errore durante il salvataggio",
                });
            }
        }
    };

    const elimina = (key) => {
        setRisultati((prev) => {
            const next = (prev || []).filter((it) => it._key !== key);
            risultatiRef.current = next;
            return next;
        });
    };

    const salvaTutti = async () => {
        if (salvandoTutti) return;
        setSalvandoTutti(true);
        try {
            const chiavi = risultatiRef.current
                .filter((it) => it._stato === "idle" && isSalvabile(it))
                .map((it) => it._key);
            for (const key of chiavi) {
                await salva(key);
            }
        } finally {
            setSalvandoTutti(false);
        }
    };

    const daSalvare = (risultati || []).filter((it) => it._stato === "idle" && isSalvabile(it)).length;

    const etichettaFile =
        files.length === 0
            ? "Seleziona PDF, JPEG o ZIP (anche multipli)"
            : files.length === 1
            ? files[0].name
            : `${files.length} file selezionati`;

    return (
        <>
            <div className="acr-panel">
                <div className="multe-upload">
                    <div className="file-input-wrapper">
                        <label htmlFor="multe-file-input" className="file-label">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span>{etichettaFile}</span>
                        </label>
                        <input
                            id="multe-file-input"
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.zip"
                            onChange={handleFileChange}
                            className="file-input"
                        />
                    </div>

                    <div className="ai-agent-actions">
                        <button className="btn-upload" onClick={handleAnalizza} disabled={files.length === 0 || uploading}>
                            {uploading ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    Elaborazione in corso…
                                </>
                            ) : (
                                "Elabora documenti"
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {errore && <div className="acr-errore">{errore}</div>}

            {risultati && risultati.length === 0 && (
                <div className="acr-info-periodo">Nessuna multa da mostrare.</div>
            )}

            {risultati && risultati.length > 0 && (
                <>
                    <div className="multe-toolbar">
                        <button
                            className="multa-btn-salva multe-btn-salva-tutti"
                            onClick={salvaTutti}
                            disabled={salvandoTutti || daSalvare === 0}
                        >
                            {salvandoTutti ? "Salvataggio…" : `Salva tutti${daSalvare ? ` (${daSalvare})` : ""}`}
                        </button>
                        <button className="multe-btn-secondario" onClick={handleReset}>
                            Nuovo caricamento
                        </button>
                    </div>

                    <div className="multe-cards">
                        {risultati.map((it) => (
                            <MultaCard
                                key={it._key}
                                item={it}
                                operatoriOptions={operatoriOptions}
                                onSalva={salva}
                                onElimina={elimina}
                                onSelezionaOperatore={selezionaOperatore}
                            />
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

// =====================================================================
// SINTESI RATE
// =====================================================================
const SintesiRate = () => {
    const [mese, setMese] = useState(meseCorrenteYM);
    const [righe, setRighe] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState("");
    const [exporting, setExporting] = useState(null); // "excel" | "pdf" | null
    // Filtri bozza
    const [filialeSel, setFilialeSel] = useState([]);
    const [tipoContrattoSel, setTipoContrattoSel] = useState([]);
    const [settoreSel, setSettoreSel] = useState([]);
    const [commessaSel, setCommessaSel] = useState([]);
    // Filtri applicati
    const [filtriAppl, setFiltriAppl] = useState({ filiale: [], tipoContratto: [], settore: [], commessa: [] });
    const [espansi, setEspansi] = useState(new Set());

    const handleApplica = async () => {
        if (!mese || loading) return;
        setLoading(true);
        setErrore("");
        try {
            const { data } = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/multe/sintesi-rate`,
                { mese }
            );
            setRighe(Array.isArray(data.rate) ? data.rate : []);
            setFiltriAppl({
                filiale: filialeSel,
                tipoContratto: tipoContrattoSel,
                settore: settoreSel,
                commessa: commessaSel,
            });
            setEspansi(new Set());
        } catch (err) {
            setErrore(err.response?.data?.error || err.message || "Errore durante la generazione");
            setRighe(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleApplica();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const opzioniFiliale = useMemo(() => opzioniDaCampoMulti(righe, "filiale"), [righe]);
    const opzioniTipoContratto = useMemo(() => opzioniDaCampoMulti(righe, "tipo_contratto"), [righe]);
    const opzioniSettore = useMemo(() => opzioniDaCampoMulti(righe, "settore"), [righe]);
    const opzioniCommessa = useMemo(() => opzioniDaCampoMulti(righe, "commessa"), [righe]);

    const filtered = useMemo(() => {
        if (!righe) return [];
        return righe.filter((r) =>
            matchCampoMulti(r.filiale, filtriAppl.filiale) &&
            matchCampoMulti(r.tipo_contratto, filtriAppl.tipoContratto) &&
            matchCampoMulti(r.settore, filtriAppl.settore) &&
            matchCampoMulti(r.commessa, filtriAppl.commessa)
        );
    }, [righe, filtriAppl]);

    const toggleEspandi = (key) => {
        setEspansi((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleExport = async (formato) => {
        if (!mese || exporting) return;
        setExporting(formato);
        try {
            const resp = await axios.post(
                `${API_URL}/agente-controllo-rendicontazioni/multe/sintesi-rate/export-${formato}`,
                { mese, righe: filtered },
                { responseType: "blob" }
            );
            const ext = formato === "pdf" ? "pdf" : "xlsx";
            saveAs(resp.data, `sintesi_rate_${mese}.${ext}`);
        } catch (err) {
            console.error(`Export ${formato} fallito:`, err);
        } finally {
            setExporting(null);
        }
    };

    return (
        <>
            <div className="acr-panel">
                <div className="acr-filters-grid">
                    <div className="acr-filter-item">
                        <label>Mese Rata</label>
                        <MonthPickerModal
                            value={mese}
                            onChange={(e) => setMese(e.target.value)}
                            label="Seleziona mese"
                            min="2026-01"
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Filiale</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniFiliale}
                            value={filialeSel}
                            onChange={(vals) => setFilialeSel(vals || [])}
                            placeholder={righe ? "Tutte" : "Caricamento…"}
                            noOptionsMessage={() => "Nessuna filiale"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Tipo Contratto</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniTipoContratto}
                            value={tipoContrattoSel}
                            onChange={(vals) => setTipoContrattoSel(vals || [])}
                            placeholder={righe ? "Tutti" : "Caricamento…"}
                            noOptionsMessage={() => "Nessun tipo"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Settore</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniSettore}
                            value={settoreSel}
                            onChange={(vals) => setSettoreSel(vals || [])}
                            placeholder={righe ? "Tutti" : "Caricamento…"}
                            noOptionsMessage={() => "Nessun settore"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-item acr-filter-item-select">
                        <label>Commessa</label>
                        <Select
                            isMulti
                            isDisabled={!righe}
                            options={opzioniCommessa}
                            value={commessaSel}
                            onChange={(vals) => setCommessaSel(vals || [])}
                            placeholder={righe ? "Tutte" : "Caricamento…"}
                            noOptionsMessage={() => "Nessuna commessa"}
                            classNamePrefix="acr-select"
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <div className="acr-filter-actions">
                        <button className="acr-btn-primary" onClick={handleApplica} disabled={!mese || loading}>
                            {loading ? "Caricamento…" : "Applica"}
                        </button>
                    </div>
                </div>
            </div>

            {errore && <div className="acr-errore">{errore}</div>}

            {righe !== null && filtered.length === 0 && !loading && (
                <div className="multe-empty">Nessuna rata nel mese selezionato.</div>
            )}

            {filtered.length > 0 && (
                <>
                <div className="acr-toolbar-sintesi">
                    <div className="acr-toolbar-title">
                        {filtered.length === righe?.length
                            ? `Rate (${filtered.length})`
                            : `Rate (${filtered.length} su ${righe?.length})`}
                    </div>
                    <div className="acr-actions-right">
                        <button
                            className="acr-export-btn"
                            onClick={() => handleExport("excel")}
                            disabled={!!exporting}
                            title="Esporta i dati in Excel"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            {exporting === "excel" ? "Esportazione…" : "Esporta Excel"}
                        </button>
                        <button
                            className="acr-export-btn acr-export-btn-pdf"
                            onClick={() => handleExport("pdf")}
                            disabled={!!exporting}
                            title="Esporta il report in PDF (layout cartaceo)"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="9" y1="13" x2="15" y2="13" />
                                <line x1="9" y1="17" x2="15" y2="17" />
                            </svg>
                            {exporting === "pdf" ? "Esportazione…" : "Esporta PDF"}
                        </button>
                    </div>
                </div>
                <div className="rate-cards">
                    {filtered.map((r) => {
                        const key = `${r.n_verbale}|${r.nome}|${r.cognome}`;
                        const aperto = espansi.has(key);
                        return (
                            <div
                                key={key}
                                className={`rate-card${r.rateizzazione_troncata ? " rate-card-troncata" : ""}`}
                            >
                                <div className="rate-card-head">
                                    <span className="rate-card-nome">{r.cognome} {r.nome}</span>
                                </div>
                                <div className="rate-card-fields">
                                    {r.filiale && (
                                        <div className="rate-card-field">
                                            <span className="rate-card-field-label">Filiale</span>
                                            <span className="rate-card-field-value">{r.filiale}</span>
                                        </div>
                                    )}
                                    {r.tipo_contratto && (
                                        <div className="rate-card-field">
                                            <span className="rate-card-field-label">Tipo Contratto</span>
                                            <span className="rate-card-field-value">{r.tipo_contratto}</span>
                                        </div>
                                    )}
                                    {r.settore && (
                                        <div className="rate-card-field">
                                            <span className="rate-card-field-label">Settore</span>
                                            <span className="rate-card-field-value">{r.settore}</span>
                                        </div>
                                    )}
                                    {r.commessa && (
                                        <div className="rate-card-field">
                                            <span className="rate-card-field-label">Commessa</span>
                                            <span className="rate-card-field-value">{r.commessa}</span>
                                        </div>
                                    )}
                                    <div className="rate-card-field">
                                        <span className="rate-card-field-label">N. Verbale</span>
                                        <span className="rate-card-field-value">{r.n_verbale}</span>
                                    </div>
                                </div>
                                <div className="rate-card-importo-row">
                                    <div>
                                        <div className="rate-card-importo-label">Importo rata {r.rata_mese?.mese}</div>
                                        <div className="rate-importo-mese">{fmtEuro(r.rata_mese?.importo)}</div>
                                    </div>
                                    <button
                                        className="expand-btn-compact"
                                        onClick={() => toggleEspandi(key)}
                                        title={aperto ? "Comprimi" : "Espandi rateizzazione"}
                                    >
                                        <svg
                                            width="16" height="16" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2"
                                            style={{ transform: aperto ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                                        >
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </button>
                                </div>

                                {aperto && (
                                    <div className="rate-card-accordion">
                                        <table className="acr-table multe-rate-table">
                                            <thead>
                                                <tr>
                                                    <th>Rata</th>
                                                    <th>Mese</th>
                                                    <th>Importo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(r.rate_complete || []).map((rata) => (
                                                    <tr
                                                        key={rata.rata_numero}
                                                        className={rata.meseYYYYMM === mese ? "rate-rata-corrente" : ""}
                                                    >
                                                        <td>{rata.rata_numero}</td>
                                                        <td>{rata.mese || "-"}</td>
                                                        <td className="multe-importo">{fmtEuro(rata.importo)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {r.rateizzazione_troncata && r.data_fine_contratto && (
                                            <div className="multa-warning-rateizzazione">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05l-8.47-14.14a2 2 0 0 0-3.42 0z" />
                                                    <line x1="12" y1="9" x2="12" y2="13" />
                                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                                </svg>
                                                <div>
                                                    <strong>Rateizzazione troncata per scadenza contratto</strong>
                                                    <p>{new Date(r.data_fine_contratto).toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" })}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                </>
            )}
        </>
    );
};

// =====================================================================
const SezioneMulte = () => {
    const [subTab, setSubTab] = useState("rate");

    return (
        <>
            <div className="acr-tabs-sub">
                <button
                    className={`acr-tab-sub ${subTab === "rate" ? "active" : ""}`}
                    onClick={() => setSubTab("rate")}
                >
                    Sintesi Rate
                </button>
                <button
                    className={`acr-tab-sub ${subTab === "caricamento" ? "active" : ""}`}
                    onClick={() => setSubTab("caricamento")}
                >
                    Caricamento Multe
                </button>
                <button
                    className={`acr-tab-sub ${subTab === "sintesi" ? "active" : ""}`}
                    onClick={() => setSubTab("sintesi")}
                >
                    Storico Multe
                </button>
            </div>

            {subTab === "rate" ? <SintesiRate /> : subTab === "caricamento" ? <CaricamentoMulte /> : <SintesiMulte />}
        </>
    );
};

export default SezioneMulte;
